// Multi-territory Exchange Rates admin tab.
// Lists one row per territory (Country / Reporting currency / Exchange rate).
// - Pencil (Edit) switches the grid into inline bulk-edit mode (same pattern as
//   Payee Master): only the Exchange rate column becomes editable, and Save /
//   Cancel icon buttons appear in the header toolbar (no footer buttons).
// - "Base currency for reports & dashboards" dropdown lets an admin pick the
//   base currency and Save it (drives the "Exchange rate (1<BASE>=)" column).
// - Download exports the grid as Excel; Search uses the shared advanced Search
//   component and sends searchCOList to both the list and download APIs.

import { saveBlobAsFile } from '@/api/apiUtils'
import { DownloadIcon, SearchIconBtn } from '@/assets/icons'
import EditIcon from '@/assets/icons/EditIcon'
import CloseIcon from '@/assets/icons/MTCancelIcon'
import SaveIcon from '@/assets/icons/MTSaveIcon'
import { Search } from '@/components/common'
import CustomButton from '@/components/Returns/CustomButton'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import {
    MT_DEFAULT_BASE_CURRENCY,
    MT_EXCHANGE_RATE_COLUMNS,
} from '@/multi-territory/mt-configs/mt-exchange-rates'
import {
    downloadExchangeRates,
    getBaseCurrency,
    getExchangeRateList,
    updateBaseCurrency,
    updateExchangeRates,
    type MTExchangeRateItem,
} from '@/multi-territory/mt-services/mt-exchange-rate-api'
import type {
    MTMetadataField,
    MTMetadataTableColumn,
} from '@/multi-territory/mt-types/mt-metadata'
import {
    buildMTSearchField,
    formatMTHeaderLabel,
    formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import { ROLE_CAPABILITIES } from '@/utils/role-capabilities'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
    Box,
    Button,
    ClickAwayListener,
    IconButton,
    MenuItem,
    Paper,
    Popper,
    Tooltip,
    Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

const ExchangeRateSearch = Search as any

export function MTAdminExchangeRates() {
  // Role gating: only editable roles see the edit pencil / base-currency Save.
  const roleTypes = useSelector((state: any) => state.user.role)
  const roleConfig =
    ROLE_CAPABILITIES[roleTypes as keyof typeof ROLE_CAPABILITIES] ||
    ROLE_CAPABILITIES.default

  // Country list loaded globally by Layout (same source as the Entity tab).
  const reduxCountryOptions: MTCountryOption[] =
    useSelector((state: any) => state.navigation.countryOptions) || []

  const { showSnackbar } = useSnackbar()

  // Data state
  const [rows, setRows] = useState<MTExchangeRateItem[]>([])
  const [loading, setLoading] = useState(false)

  // Base currency: the committed value + the pending dropdown selection.
  const [baseCurrency, setBaseCurrency] = useState<string>(
    MT_DEFAULT_BASE_CURRENCY
  )
  const [currencyAnchor, setCurrencyAnchor] = useState<HTMLElement | null>(null)
  const [pendingCurrency, setPendingCurrency] = useState<string>(
    MT_DEFAULT_BASE_CURRENCY
  )
  // Selectable currencies + rate column label come from the API.
  const [currencyOptions, setCurrencyOptions] = useState<string[]>([])
  const [rateLabel, setRateLabel] = useState<string>('')

  // Inline bulk-edit state (mirrors Payee Master):
  // editMode toggles the grid into edit mode; editingData holds the per-row
  // changed values keyed by rowId -> { fieldKey: value }.
  const [editMode, setEditMode] = useState(false)
  const [editingData, setEditingData] = useState<Record<string, any>>({})

  // Advanced search (shared Search component, same as the Entity tab).
  // appliedSearch is the searchCOList sent to the list + download APIs.
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchAnchor, setSearchAnchor] = useState<HTMLElement | null>(null)
  const [appliedSearch, setAppliedSearch] = useState<any[]>([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: MT_EXCHANGE_RATE_COLUMNS[0]?.id || '',
      value: '',
    },
  ])

  // --- Fetch ---
  const fetchExchangeRates = useCallback(async () => {
    setLoading(true)

    // Currency dropdown options load independently so they never block the grid.
    getBaseCurrency()
      .then(baseRes => {
        if (baseRes.success) {
          setCurrencyOptions(baseRes.data?.availableCurrencies ?? [])
        }
      })
      .catch(() => undefined)

    try {
      const listRes = await getExchangeRateList({
        // Whole list in one page; filtering is done server-side via searchCOList.
        pageFilter: {
          currentPage: 1,
          pageSize: 1000,
          sortField: 'country',
          sortOrder: 'ASC',
        },
        searchCOList: appliedSearch,
      })
      if (listRes.success) {
        setRows(listRes.data?.data ?? [])
        if (listRes.data?.baseCurrency) {
          setBaseCurrency(listRes.data.baseCurrency)
          setPendingCurrency(listRes.data.baseCurrency)
        }
        if (listRes.data?.rateColumnLabel) {
          setRateLabel(listRes.data.rateColumnLabel)
        }
      } else {
        showSnackbar(listRes.error || 'Failed to load exchange rates', 'error')
      }
    } catch {
      showSnackbar('Failed to load exchange rates', 'error')
    } finally {
      setLoading(false)
    }
  }, [showSnackbar, appliedSearch])

  useEffect(() => {
    fetchExchangeRates()
  }, [fetchExchangeRates])

  // --- Derived: rows for the grid ---
  const visibleRows = useMemo(() => {
    // Normalise for display; countryCode is the row key (no uuid in this API).
    return rows.map(row => ({
      ...row,
      id: row.countryCode,
      reportingCurrency: (row.reportingCurrency ?? '').trim(),
      exchangeRate:
        row.exchangeRate == null || row.exchangeRate === ''
          ? ''
          : String(row.exchangeRate),
    }))
  }, [rows])

  // Dynamic rate column label reflects the committed base currency.
  const rateColumnLabel = rateLabel || `Exchange rate (1${baseCurrency}=)`

  // Table columns for MTCommonTable. Only the Exchange rate column is editable.
  const tableColumns = useMemo<MTMetadataTableColumn[]>(
    () =>
      MT_EXCHANGE_RATE_COLUMNS.map(col => ({
        id: col.id,
        label: col.id === 'exchangeRate' ? rateColumnLabel : col.label,
        type:
          col.id === 'exchangeRate' ? ('number' as const) : ('text' as const),
        sortable: false,
        editable: col.id === 'exchangeRate',
      })),
    [rateColumnLabel]
  )

  // Metadata fields so MTCommonTable knows which cells are editable in edit mode.
  const rawMetadataFields = useMemo<MTMetadataField[]>(
    () =>
      tableColumns.map((col, idx) => ({
        field_key: col.id,
        label: col.label,
        type: col.type,
        required: false,
        placeholder: col.id === 'exchangeRate' ? '--' : '',
        grid_column: 1,
        order: idx,
        editable: col.editable,
        visible: true,
        auto_fill: null,
        depends_on: null,
        validations: {},
      })),
    [tableColumns]
  )

  // --- Edit handlers ---
  const handleEditClick = (): void => {
    setEditMode(true)
    setEditingData({})
  }

  const handleCancelEdit = (): void => {
    setEditMode(false)
    setEditingData({})
  }

  const handleFieldChange = (
    rowId: string,
    fieldKey: string,
    value: string
  ): void => {
    setEditingData(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [fieldKey]: value },
    }))
  }

  const handleSaveEdit = async (): Promise<void> => {
    const entries = Object.entries(editingData || {})
    if (entries.length === 0) {
      showSnackbar('No changes to save', 'warning')
      return
    }
    const rates = entries
      .filter(([, changes]) => (changes as any).exchangeRate !== undefined)
      .map(([countryCode, changes]) => ({
        countryCode,
        raw: String((changes as any).exchangeRate ?? '').trim(),
      }))

    if (rates.length === 0) {
      showSnackbar('No changes to save', 'warning')
      return
    }

    // A rate must be a number greater than 0.
    if (rates.some(r => r.raw === '' || !(Number(r.raw) > 0))) {
      showSnackbar('Exchange rate must be a number greater than 0', 'error')
      return
    }

    const response = await updateExchangeRates({
      rates: rates.map(r => ({
        countryCode: r.countryCode,
        rate: Number(r.raw),
      })),
    })
    if (response.success) {
      showSnackbar(response.data || 'Exchange rates updated', 'success')
      handleCancelEdit()
      fetchExchangeRates()
    } else {
      showSnackbar(response.error || 'Failed to update exchange rates', 'error')
    }
  }

  // --- Base currency handlers ---
  const handleCurrencyOpen = (e: React.MouseEvent<HTMLElement>): void => {
    setPendingCurrency(baseCurrency)
    setCurrencyAnchor(currencyAnchor ? null : e.currentTarget)
  }

  const handleCurrencyClose = (): void => {
    setCurrencyAnchor(null)
    setPendingCurrency(baseCurrency)
  }

  const handleCurrencySave = async (): Promise<void> => {
    if (pendingCurrency === baseCurrency) {
      setCurrencyAnchor(null)
      return
    }
    const response = await updateBaseCurrency({
      baseCurrency: pendingCurrency,
      confirmed: true,
    })
    if (response.success) {
      showSnackbar(response.data || 'Base currency updated', 'success')
      setBaseCurrency(pendingCurrency)
      setCurrencyAnchor(null)
      fetchExchangeRates()
    } else {
      showSnackbar(response.error || 'Failed to update base currency', 'error')
    }
  }

  // --- Download ---
  const handleDownload = async (): Promise<void> => {
    const response = await downloadExchangeRates(appliedSearch)
    if (response.success) {
      saveBlobAsFile(response.data, 'exchange-rates.xlsx')
    } else {
      showSnackbar('Failed to download', 'error')
    }
  }

  // --- Search ---
  const handleSearchOpen = (e: React.MouseEvent<HTMLElement>): void => {
    setSearchAnchor(e.currentTarget)
    setSearchOpen(true)
  }

  const handleSearchClose = (): void => {
    setSearchOpen(false)
    setSearchAnchor(null)
  }

  const handleApplySearch = (criteria: any[]): void => {
    const apiCriteria = formatMTSearchCriteria(criteria)
    setSearchCriteria(
      apiCriteria.map((item: any, idx: number) => ({
        ...item,
        field: item.fieldName || item.field || '',
        id: item.id || idx + 1,
        logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
      }))
    )
    setAppliedSearch(apiCriteria)
    handleSearchClose()
  }

  const handleResetSearch = (): void => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: MT_EXCHANGE_RATE_COLUMNS[0]?.id || '',
        value: '',
      },
    ])
    setAppliedSearch([])
    handleSearchClose()
  }

  // Search fields mirror the grid columns; the rate label tracks the base currency.
  const searchFields = MT_EXCHANGE_RATE_COLUMNS.map(col => {
    // Country is a dropdown of the user's countries (same as the Entity tab).
    if (col.id === 'country') {
      return {
        ...col,
        label: formatMTHeaderLabel(col.label),
        type: 'dropdown',
        placeholder: 'Select country',
        options: reduxCountryOptions.map(c => ({
          label: c.country_name,
          value: c.country_name,
        })),
      }
    }
    return buildMTSearchField({
      ...col,
      label: col.id === 'exchangeRate' ? rateColumnLabel : col.label,
    })
  })

  const currencyOpen = Boolean(currencyAnchor)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Header row: title + base-currency selector + action icons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 500,
            lineHeight: '1.3em',
            color: 'var(--forbrand-secondary)',
            whiteSpace: 'nowrap',
          }}
        >
          {GLOBAL_CONSTANTS.MT_ADMIN_EXCHANGE_RATES_TITLE}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--forbrand-secondary)',
              whiteSpace: 'nowrap',
            }}
          >
            {GLOBAL_CONSTANTS.MT_ADMIN_EXCHANGE_RATES_BASE_CURRENCY_LABEL}
          </Typography>

          {/* Base currency trigger */}
          <Button
            onClick={handleCurrencyOpen}
            disabled={editMode}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              minWidth: 110,
              justifyContent: 'space-between',
              textTransform: 'none',
              color: 'var(--forbrand-primary)',
              border: '1px solid #d0d0d0',
              borderRadius: 0,
              px: 1.5,
              height: 40,
              backgroundColor: '#fff',
            }}
          >
            {baseCurrency}
          </Button>

          <Popper
            open={currencyOpen}
            anchorEl={currencyAnchor}
            placement="bottom-end"
            style={{ zIndex: 1300 }}
          >
            <ClickAwayListener onClickAway={handleCurrencyClose}>
              <Paper
                elevation={3}
                sx={{
                  mt: 0.5,
                  minWidth: 170,
                  border: '1px solid var(--border-strong, #8E95A2)',
                  borderRadius: '4px',
                }}
              >
                <Box sx={{ maxHeight: 220, overflowY: 'auto', py: 0.5 }}>
                  {currencyOptions.map(currency => (
                    <MenuItem
                      key={currency}
                      selected={pendingCurrency === currency}
                      onClick={() => setPendingCurrency(currency)}
                      sx={{
                        fontSize: '14px',
                        '&.Mui-selected': { backgroundColor: '#f3f4f6' },
                      }}
                    >
                      {currency}
                    </MenuItem>
                  ))}
                </Box>
                {roleConfig.canEdit && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      p: 1,
                      borderTop: '1px solid var(--border-subtel)',
                    }}
                  >
                    <CustomButton size="small" onClick={handleCurrencySave}>
                      {GLOBAL_CONSTANTS.SAVE}
                    </CustomButton>
                  </Box>
                )}
              </Paper>
            </ClickAwayListener>
          </Popper>

          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <IconButton size="medium" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          {/* Edit -> Save/Cancel icon toolbar (bulk-edit, Payee Master pattern) */}
          {roleConfig.canEdit && !editMode && (
            <Tooltip title={GLOBAL_CONSTANTS.EDIT}>
              <span>
                <IconButton
                  size="medium"
                  onClick={handleEditClick}
                  disabled={loading}
                  aria-label="Edit"
                  sx={{ color: 'var(--forbrand-primary)' }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {roleConfig.canEdit && editMode && (
            <>
              <Tooltip title={GLOBAL_CONSTANTS.SAVE}>
                <IconButton
                  size="medium"
                  onClick={handleSaveEdit}
                  aria-label="Save"
                  sx={{ color: 'var(--forbrand-primary)' }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={GLOBAL_CONSTANTS.MT_ADMIN_EXCHANGE_RATES_CANCEL}>
                <IconButton
                  size="medium"
                  onClick={handleCancelEdit}
                  aria-label="Cancel"
                  sx={{ color: 'var(--forbrand-primary)' }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton size="medium" onClick={handleSearchOpen}>
              <SearchIconBtn />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <ExchangeRateSearch
        open={searchOpen}
        anchorEl={searchAnchor}
        onClose={handleSearchClose}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        fields={searchFields}
        placement="bottom-end"
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
      />

      {/* Grid: inline bulk edit via MTCommonTable (no footer Save/Cancel) */}
      <MTCommonTable
        columns={tableColumns}
        data={visibleRows}
        loading={loading}
        editMode={editMode}
        editingData={editingData}
        onFieldChange={handleFieldChange}
        rawMetadataFields={rawMetadataFields}
        showFooter={false}
        emptyMessage="No exchange rates found"
      />
    </Box>
  )
}

export default MTAdminExchangeRates
